import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  MailIcon,
  UserCircleIcon,
  UserPen,
} from "lucide-react";
import InAppChangePassword from "./common/InAppChangePassword";
import { useAuthState } from "@/context";
import { PhoneIcon } from "@/assets";
import SetUpTwoFA from "./common/SetUpTwoFA";
import { getMerchant } from "@/services/Home.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { HiOutlineSlash } from "react-icons/hi2";
import { Link } from "react-router";
import { formattedDate, formattedTransactionDate } from "@/lib/helper";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { generalSetting, getGeneralSetting } from "@/services/Settings.service";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import { ToastMessage, type } from "@/lib/toastr";

const Profile = () => {
  const { state } = useAuthState();
  const [appName, setAppName] = useState("");
  const [domain, setDomain] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [edit, setEdit] = useState(true);
  const user = useQuery({
    queryKey: ["merchant_user_Profile"],
    queryFn: async () => await getMerchant(),
  });
  const merchantlocal = state?.merchant;
  const userlocal = state?.user;

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["getGeneralSettings"],
    queryFn: async () => await getGeneralSetting(),
  });

  const mutation = useMutation({
    mutationFn: (formData) => generalSetting(formData),
    mutationKey: ["generalSetting"],
    onSuccess: async (data) => {
      if (data?.data?.statusCode === 200)
        ToastMessage(type.SUCCESS, "Settings saved successfully");
      setTimeout(() => setEdit(true), 1000);
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  useEffect(() => {
    setAppName(data?.data?.data?.appName);
    setDomain(data?.data?.data?.domain);
    setSupportEmail(data?.data?.data?.supportEmail);
  }, [
    data?.data?.data?.appName,
    data?.data?.data?.domain,
    data?.data?.data?.supportEmail,
  ]);

  const handleEditSubmit = () => {
    const data = {
      appName,
      domain,
      supportEmail,
    };
    if (edit) {
      setEdit(false);
    } else {
      mutation.mutate(data);
    }
  };
  return (
    <div className="flex flex-col gap-8 p-6">
      <div>
        <Breadcrumb>
          <BreadcrumbList className="gap-2">
            <BreadcrumbItem>
              <Link
                to="/dashboard/settings"
                className="flex items-center gap-1 text-sm text-primary0"
              >
                <ChevronLeft className="w-3 h-3" />
                Settings
              </Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <HiOutlineSlash />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink className="flex items-center gap-1 text-sm text-gray-60">
                General
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className=" flex gap-8">
        <div className="flex items-center gap-6 bg-off-white-1 p-6 rounded-[12px] h-fit w-[418px]">
          <Avatar className="w-24 h-24 border-2 border-primary capitalize">
            <AvatarImage />
            <AvatarFallback>
              {userlocal?.firstName[0]} {userlocal?.lastName[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1">
            <p className="text-xl font-medium text-gray-1 capitalize">
              {userlocal?.firstName} {userlocal?.lastName}
            </p>
            <div className="flex items-center gap-2">
              <MailIcon className="text-primary" />
              <p className="text-sm text-gray-2">{userlocal?.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <UserCircleIcon className="text-primary" />
              <p className="text-sm text-gray-2 capitalize">
                {merchantlocal?.roleId?.split("-")[1]}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="text-primary" />
              <p className="text-sm text-gray-2">
                joined {formattedTransactionDate(userlocal?.createdAt)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid w-[541px]">
          {state?.merchant?.roleId !== "merchant-owner" ? null : (
            <div className="grid bg-off-white-1 p-6 rounded-[12px] gap-4 w-[541px]">
              <div className="flex flex-col gap-1">
                <h3 className="text-base text-primary-3">
                  Business information
                </h3>
                <p className="text-sm text-gray-80">
                  View and manage your business information here
                </p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {isLoading ? (
                  <Skeleton className="h-14 w-full" />
                ) : (
                  <div
                    className={cn(
                      "flex flex-col w-full items-start gap-1 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
                    )}
                  >
                    <Label className="text-xs">Business Name</Label>

                    <Input
                      type="text"
                      className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                      defaultValue={data?.data?.data?.name}
                      disabled
                    />
                  </div>
                )}

                {isLoading ? (
                  <Skeleton className="h-14 w-full" />
                ) : (
                  <div
                    className={cn(
                      "flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
                    )}
                  >
                    <Label className="text-xs">Business ID</Label>

                    <Input
                      type="text"
                      className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                      defaultValue={data?.data?.data?.id}
                      disabled
                    />
                  </div>
                )}
                {isLoading ? (
                  <Skeleton className="h-14 w-full" />
                ) : (
                  <div
                    className={cn(
                      "flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
                    )}
                  >
                    <Label className="text-xs">Business Email</Label>

                    <Input
                      type="text"
                      className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                      defaultValue={data?.data?.data?.email}
                      disabled
                    />
                  </div>
                )}
                {isLoading ? (
                  <Skeleton className="h-14 w-full" />
                ) : (
                  <div
                    className={cn(
                      "flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
                    )}
                  >
                    <Label className="text-xs">App Name</Label>

                    <Input
                      type="text"
                      className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                      value={appName}
                      disabled={edit}
                      onChange={(e) => setAppName(e?.target?.value)}
                    />
                  </div>
                )}

                {isLoading ? (
                  <Skeleton className="h-14 w-full" />
                ) : (
                  <div
                    className={cn(
                      "flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
                    )}
                  >
                    <Label className="text-xs">App Domain</Label>

                    <Input
                      type="text"
                      className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                      value={domain}
                      disabled={edit}
                      onChange={(e) => setDomain(e?.target?.value)}
                    />
                  </div>
                )}
                {isLoading ? (
                  <Skeleton className="h-14 w-full" />
                ) : (
                  <div
                    className={cn(
                      "flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14 bg-white border-input-border"
                    )}
                  >
                    <Label className="text-xs">Support Email</Label>

                    <Input
                      type="text"
                      className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                      value={supportEmail}
                      disabled={edit}
                      onChange={(e) => setSupportEmail(e?.target?.value)}
                    />
                  </div>
                )}
              </div>

              <Button
                className=" bg-transparent text-primary0 border-[1px] border-primary0 hover:text-primary0"
                onClick={handleEditSubmit}
              >
                {mutation?.isPending && <Loader2 className="animate-spin" />}
                <UserPen /> {edit ? "Edit" : "Save"} Details
              </Button>
            </div>
          )}
          <div className="flex flex-col bg-background border-off-white border-[1px] rounded-[12px] p-8 gap-12">
            <div className="grid gap-1">
              <h3 className="text-base text-primary-3">Password & Security</h3>
              <p className="text-sm text-gray-80">
                Manage your security credentials
              </p>
            </div>
            <div className="grid gap-8">
              <div className="grid gap-6">
                <p className="text-sm font-medium text-gray-1">
                  Reset Password
                </p>
                <InAppChangePassword />
              </div>
              <Separator />
              <div className="grid gap-6">
                <div className="">
                  <p className="text-sm font-medium text-gray-1 flex gap-2 items-center">
                    Two Factor Authentication Setup{" "}
                    {user?.data?.data?.data === undefined ? (
                      <Skeleton className="w-8 h-6" />
                    ) : (
                      <span
                        className={cn(
                          "text-xs font-normal  px-2 h-6 rounded-3xl ml-2 flex justify-center items-center",
                          user?.data?.data?.data?.twoFactorAuthentication
                            ? "bg-green0 text-green-2"
                            : "bg-error-0 text-error"
                        )}
                      >
                        {user?.data?.data?.data?.twoFactorAuthentication
                          ? "Active"
                          : "Inactive"}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-3">
                    Enable Two- Factor Authentication for login
                  </p>
                </div>
                {user?.data?.data?.data === undefined ? (
                  <Skeleton className="w-full h-10 lg:h-20" />
                ) : (
                  <>
                    <SetUpTwoFA />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
